/**
 * "A new version is available" notice.
 *
 * There is exactly one update mechanism: the service worker. It detects a new
 * build, and this prompts before applying it — reloading unannounced would
 * discard a half-filled receiving screen. The registration itself lives in
 * `UpdateProvider`, which the sidebar version badge also reads.
 *
 * The Base44 build ran three mechanisms against one database row: a blocking
 * full-screen modal mounted app-wide, a second hand-built DOM modal on two
 * pages only, and a third fetch just to render a version string in the sidebar.
 * The blocking modal also fired for every first-time visitor, because it read
 * "no version stored locally" as "out of date".
 */

import { Loader2, RefreshCw, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useAppUpdate } from '@/lib/update-context.jsx';

export function UpdateToast() {
  const { updateReady, updating, applyUpdate, dismissUpdate } = useAppUpdate();

  // Nothing renders until there is genuinely a newer build waiting.
  if (!updateReady) return null;

  return (
    <div
      role="status"
      // Clear of the iPhone home indicator when installed to the home screen.
      className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] left-1/2 z-50 w-[min(26rem,calc(100vw-2rem))] -translate-x-1/2"
    >
      <div className="flex items-start gap-3 rounded-xl border bg-card p-4 shadow-xl">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-300">
          <RefreshCw className="h-4 w-4" />
        </div>

        <div className="flex-1">
          <p className="text-sm font-medium">A newer version of StockWise is available</p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Reload when convenient. Anything you are part-way through will be lost.
          </p>
          {/* Taking over can take a few seconds on a phone. A button that
              did nothing visible in that time looked like a stuck update. */}
          <Button size="sm" className="mt-3" onClick={applyUpdate} disabled={updating}>
            {updating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            {updating ? 'Updating…' : 'Reload now'}
          </Button>
        </div>

        {updating ? null : (
          <button
            type="button"
            onClick={dismissUpdate}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted coarse:p-2.5"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
